import React from 'react';
import { gql } from '@soundxyz/gql-string';
import { ListContent } from '@components/Feed/ListViews/components/ListContent';
import { ListMinimalUser } from '@components/Feed/ListViews/components/ListMinimalUser';
import { Ordinal } from '@components/Feed/ListViews/components/Oridinal';
import { ReleaseInfo } from '@components/Feed/ListViews/components/ReleaseInfo';
import { ReleasePill } from '@components/Feed/ListViews/components/ReleasePill';
import {
  Container,
  CoverContainer,
  DisplayWrapper,
  GoldenEggWrapper,
  SubText,
  Wrapper,
} from '@components/Feed/ListViews/components/shared';
import { ReleasePlayButton } from '@components/basics/ReleasePlayButton';
import { MinimalEgg } from '@components/icons/Feed/minimalEgg';
import { MediaQuery } from '@context/mediaQuery';
import type { FragmentType } from '@graphql/reactQuery';
import {
  getFragment,
  ReleasePillFragmentDoc,
  SecondarySaleListFragmentDoc,
} from '@graphql/reactQuery';
import { SPACE } from '@utils/constants';
import { formatWeiToEthDisplayFree } from '@utils/helpers';
import { formatTierNumber } from '../shared';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment SecondarySaleList on SecondarySale {
    id
    user {
      id
      ...MinimalUser
    }
    purchasedFromUser {
      id
      publicAddress
      webappUri
      ...MinimalUser
    }
    release {
      id
      goldenEggNft {
        id
        tokenId
      }
      ...ReleasePill
    }
    amountPaidInWei
    fromTokenId
    fromSerialNumber
    tierNumber
    toTokenId
    toSerialNumber
    quantity
    date
  }
`);

export const SecondarySaleList = ({
  activityFrag,
}: {
  activityFrag: FragmentType<SecondarySaleListFragmentDoc>;
}) => {
  const { tabletLandscape, tablet } = MediaQuery.useContainer();

  const {
    release,
    date,
    amountPaidInWei,
    fromTokenId,
    toTokenId,
    tierNumber,
    fromSerialNumber,
    quantity,
    user,
    purchasedFromUser,
  } = getFragment(SecondarySaleListFragmentDoc, activityFrag);
  const releaseData = getFragment(ReleasePillFragmentDoc, release);

  const { title, artist } = releaseData;

  const goldenEgg =
    !!release.goldenEggNft?.tokenId &&
    BigInt(release.goldenEggNft.tokenId) >= fromTokenId &&
    BigInt(release.goldenEggNft.tokenId) <= toTokenId;

  const price = formatWeiToEthDisplayFree({ weiValue: amountPaidInWei, maxDecimals: 4 });

  const editionLabel = formatTierNumber(tierNumber);

  return (
    <Container>
      <CoverContainer>
        <ReleasePlayButton
          playButtonSize={tablet ? 'm' : 's'}
          releaseFrag={releaseData}
          goldenEgg={goldenEgg}
        />
      </CoverContainer>

      <ListContent isoDateString={date} truncateChildren>
        <Wrapper margin="first">
          <ListMinimalUser userFrag={user} />
        </Wrapper>
        <SubText>
          purchased
          {SPACE}
        </SubText>
        {quantity === 1 && (
          <>
            {editionLabel} <Ordinal edition={fromSerialNumber ?? fromTokenId} />
          </>
        )}
        {quantity > 1 && (
          <SubText>
            {quantity} {editionLabel}editions{SPACE}
          </SubText>
        )}

        <SubText>of{SPACE}</SubText>
        <DisplayWrapper media="tabletLandscape">
          <Wrapper>
            <ReleasePill release={release} goldenEgg={goldenEgg} />
          </Wrapper>
        </DisplayWrapper>

        {!tabletLandscape && !!artist.name && (
          <>
            <ReleaseInfo
              releaseTitle={title}
              releaseUri={releaseData.webappUri}
              artist={{
                artistName: artist.name,
                artistUri: artist.webappUri,
              }}
            />
            <span>{SPACE}</span>
          </>
        )}
        {goldenEgg && (
          <DisplayWrapper media="notTabletLandscape">
            <GoldenEggWrapper>
              <MinimalEgg />
            </GoldenEggWrapper>
            {SPACE}
          </DisplayWrapper>
        )}

        <SubText>from{SPACE}</SubText>

        <Wrapper>
          <ListMinimalUser userFrag={purchasedFromUser} />
          {!tabletLandscape && SPACE}
        </Wrapper>
        <SubText>for{SPACE}</SubText>
        <SubText bold>{price}</SubText>
      </ListContent>
    </Container>
  );
};
